import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import fundingBriefImg from "./images/FUNDING BRIEF.jpeg";
import './App.css';

// ✅ Chart image (modal-only, no preview until clicked)
import fundingTimelineImg from './images/Development & Commercialization Timeline.jpg';

// ✅ Blueprint diagram image (modal-only)
import tripleLayerBlueprintDiagramImg from './images/Triple-LayerMasterBlueprint.jpg';

// ✅ NEW: Use of Funds image (replaces Funding Brief PDF in Section 2)
import useOfFundsImg from './images/FUNDING BRIEF.jpeg';

const quickValues = [
{
title: 'For Platforms',
text: 'Patent-defensible fairness layer to meet regulation, restore user trust, and reduce legal risk.'
},
{
title: 'For Governments',
text: 'Transparent eligibility and redistribution logic to reduce inequality and improve public trust.'
},
{
title: 'For Funders',
text: 'Lean MVP, licensing-first model, measurable social value, and commercial upside.'
}
];

const engines = [
{
id: 'afce',
name: 'Algorithmic Fairness Constraint Engine (AFCE)',
line: 'Applies fairness constraints, reweights outcomes, and validates group parity thresholds.',
note: 'Includes plug-ins for statistical parity, equalized odds, and custom constraints.',
ring: 'center'
},
{
id: 'transformation',
name: 'Transformation Engine',
line: 'Normalizes inputs and converts raw data into standardized indicators for downstream logic.',
note: 'Accepts JSON input and returns normalized feature vectors.',
ring: 'outer'
},
{
id: 'identity',
name: 'Identity Engine',
line: 'Manages privacy-preserving identity tokens and role attributes for population segments.',
note: 'Supports hashed identifiers and differential privacy hooks.',
ring: 'outer'
},
{
id: 'eligibility',
name: 'Eligibility Engine',
line: 'Applies rule-based and parametric logic to determine qualification for programs, visibility, or benefits.',
note: 'Rules stored in JSON or YAML; editable without code changes.',
ring: 'outer'
},
{
id: 'redistribution',
name: 'Redistribution Engine',
line: 'Computes equitable allocation adjustments to reduce inequality across groups.',
note: 'Uses constraint solvers and linear programming for allocation balancing.',
ring: 'outer'
},
{
id: 'monitoring',
name: 'Monitoring Engine',
line: 'Tracks fairness metrics, inequality indicators, drift, and audit logs.',
note: 'Exposes metrics and an audit trail for compliance.',
ring: 'outer'
},
{
id: 'provision',
name: 'Provision Engine',
line: 'Executes approved outputs: allocations, ranking changes, or eligibility notifications.',
note: 'Integrates with platform APIs.',
ring: 'outer'
}
];

const diagramNodes = [
{ id: 'transformation', label: 'Transformation', color: '#3A8D8C', tooltip: 'Standardizes and prepares inputs for governed decision-making.' },
{ id: 'identity', label: 'Identity', color: '#4A5DA8', tooltip: 'Manages identity integrity and contextual attribution.' },
{ id: 'eligibility', label: 'Eligibility', color: '#4C7DA6', tooltip: 'Determines qualification using governed criteria.' },
{ id: 'redistribution', label: 'Redistribution', color: '#C2A55F', tooltip: 'Adjusts allocations to correct structural imbalances.' },
{ id: 'monitoring', label: 'Monitoring', color: '#6C7A89', tooltip: 'Tracks outcomes, drift, and compliance over time.' },
{ id: 'provision', label: 'Provision', color: '#6FAF98', tooltip: 'Delivers final, auditable outcomes to users.' }
];

const roadmap = [
{ month: 'March 2026', detail: 'Developer onboarding; Transformation Engine prototype.' },
{ month: 'April 2026', detail: 'Eligibility Engine development and integration with Transformation.' },
{ month: 'May 2026', detail: 'AFCE implementation, testing, public demo, and 2-3 minute demo video.' }
];

const licensingPillars = [
{ title: 'Patent protection', text: 'Full IP ownership retained by USECHAT (Pty) Limited.' },
{ title: 'Responsible deployment', text: 'Integration support, fairness validation, and annual audits.' },
{ title: 'Flexible terms', text: 'Exclusive, territorial, and government pricing options.' }
];

const team = [
{ name: 'Olakunle Nathaniel Olamiju', role: 'Founder and Inventor (Certified Inventor, MBA, Strategic Consultant).', bio: 'Inventor-led product strategy, governance-grade fairness design, and licensing.' },
{ name: 'Technical Lead (TBA)', role: 'Backend and fairness modeling.', bio: 'Responsible for AFCE integration, eligibility logic, and monitoring stack.' },
{ name: 'Wil Jacques', role: 'Licensing and M&A Advisor (engagement pending).', bio: 'Guides licensing strategy, valuation, and partner negotiations.' },
{ name: 'Patent Counsel (TBA)', role: 'Formal counsel of record.', bio: 'Oversees patent filings, claims strategy, and freedom-to-operate reviews.' }
];

const fadeUp = {
hidden: { opacity: 0, y: 32 },
visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } }
};

const fadeIn = {
hidden: { opacity: 0 },
visible: { opacity: 1, transition: { duration: 0.7, ease: 'easeOut' } }
};

// ✅ PDFs in /public/pdfs
const PDF_FILES = {
'1': 'PDF 1 - Systems Engineering Compliance Statement.pdf',
'1.5': 'USECHAT - Patent Summary (Section 1.5).pdf',
'2': 'PDF 2 - Responsible AI Compliance Statement.pdf',
'3': 'PDF 3 - MVP Technical Specification Specifications.pdf',
'4.1': 'PDF 4.1 - Usechat Alignment with South Africa’s National Development Plan (NDP).pdf',
'4.2': 'PDF 4.2 - Usechat Universal Government Value & Application Summary.pdf',
'4.3': 'PDF 4.3 - Usechat One Page Executive Brief.pdf',
'4.4': 'PDF 4.4 - Usechat Ministerial Talking Points.pdf',
'4.5': 'PDF 4.5 - Treasury & Finance Justification Note.pdf',
'5': 'USECHAT - Patent Summary (Section 1.5).pdf',
'6': 'PDF 6 - Funding Brief.pdf',
'7': 'PDF 7 - Job Creation & Economic Impact Brief.pdf',
'8': 'PDF 8 - Valuation & Commercialization Overview.pdf',
'9': 'PDF 9 - Acquisition & Licensing Strategy Overview.pdf',
'12': 'PDF 12 - Triple-Layer Master Blueprint.pdf',
'13': 'PDF 13 - Foundational Governance Rationale.pdf',
'14': 'PDF 14 - Inequality & Fairness Framework.pdf',
'15': 'PDF 15 - Ethical & Societal Context Brief.pdf',
'16': 'PDF 16 - Founder’s Statement.pdf',
'17': 'PDF 17 - Storytelling Document.pdf',
'18': 'PDF 18 - USECHAT Public Press Announcement.pdf',

// ✅ NEW (Section 4 A.1)
  A1: 'USECHAT - What is USECHAT (Section 4.A.1).pdf'
};

const WHATSAPP_NUMBER = "27714829343"; 
const WHATSAPP_DEFAULT_MESSAGE = "Hi USECHAT — I’m visiting your website and I’d like to chat.";

function App() {
  const openWhatsAppChat = () => {
  const text = encodeURIComponent(WHATSAPP_DEFAULT_MESSAGE);
  const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

const [activeEngine, setActiveEngine] = useState('afce');
const [navOpen, setNavOpen] = useState(false);

// ✅ Modals
const [pressDiagramOpen, setPressDiagramOpen] = useState(false); // orbit diagram
const [fundingChartOpen, setFundingChartOpen] = useState(false);
const [blueprintDiagramOpen, setBlueprintDiagramOpen] = useState(false);

// ✅ NEW: Use of Funds modal (replaces Funding Brief PDF in Section 2)
const [useOfFundsOpen, setUseOfFundsOpen] = useState(false);

const [fundingBriefOpen, setFundingBriefOpen] = useState(false);

// ✅ NEW: Welcome popup
const [welcomeOpen, setWelcomeOpen] = useState(false);
const welcomeSoundRef = useRef(null);

const active = useMemo(
() => engines.find((engine) => engine.id === activeEngine) || engines[0],
[activeEngine]
);

// Auto-rotate active engine
useEffect(() => {
const timer = setInterval(() => {
setActiveEngine((prev) => {
const currentIndex = engines.findIndex((engine) => engine.id === prev);
const nextIndex = (currentIndex + 1) % engines.length;
return engines[nextIndex].id;
});
}, 3200);
return () => clearInterval(timer);
}, []);

// ✅ NEW: Welcome popup (shows once unless dismissed)
useEffect(() => {
const dismissed = localStorage.getItem('usechat_welcome_dismissed') === '1';
if (dismissed) return;

const t = setTimeout(() => {
setWelcomeOpen(true);
welcomeSoundRef.current?.play?.().catch(() => {});
}, 3000);

return () => clearTimeout(t);
}, []);

const closeWelcome = () => {
setWelcomeOpen(false);
localStorage.setItem('usechat_welcome_dismissed', '1');
};

// ✅ ESC closes any open modal + lock scroll while any modal open
useEffect(() => {
const anyOpen = pressDiagramOpen || fundingChartOpen || blueprintDiagramOpen || useOfFundsOpen;
if (!anyOpen) return;

const onKeyDown = (e) => {
if (e.key === 'Escape') {
setPressDiagramOpen(false);
setFundingChartOpen(false);
setBlueprintDiagramOpen(false);
setUseOfFundsOpen(false);
}
};

window.addEventListener('keydown', onKeyDown);

const prevOverflow = document.body.style.overflow;
document.body.style.overflow = 'hidden';

return () => {
window.removeEventListener('keydown', onKeyDown);
document.body.style.overflow = prevOverflow;
};
}, [pressDiagramOpen, fundingChartOpen, blueprintDiagramOpen, useOfFundsOpen]);

const closeNav = () => setNavOpen(false);

const [submitStatus, setSubmitStatus] = useState({ state: "idle", error: "" });
// states: idle | sending | success | error

const handleFormSubmit = async (event) => {
event.preventDefault();

const formEl = event.currentTarget;
const formData = new FormData(formEl);

if (formData.get("hp_token")) return; // honeypot

// NOTE: we are NOT sending file attachments yet (keeps it reliable on Vercel)
const payload = {
organization: formData.get("organization"),
contact: formData.get("contact"),
email: formData.get("email"),
phone: formData.get("phone"),
country: formData.get("country"),
inquiry: formData.get("inquiry"),
message: formData.get("message"),
};

setSubmitStatus({ state: "sending", error: "" });

try {
const r = await fetch("/api/Contact", {
method: "POST",
headers: { "Content-Type": "application/json" },
body: JSON.stringify(payload),
});

const data = await r.json().catch(() => ({}));

if (!r.ok || !data.ok) {
throw new Error(data.error || "Could not send message");
}

setSubmitStatus({ state: "success", error: "" });
formEl.reset();
} catch (e) {
setSubmitStatus({ state: "error", error: e.message || "Error" });
}
};

const pdfHref = (pdfKey) => {
const filename = PDF_FILES[pdfKey];
if (!filename) return '#';
return `${process.env.PUBLIC_URL}/pdfs/${encodeURIComponent(filename)}`;
};

// PDF 11 = Video placeholder
const DEMO_VIDEO_URL = '#';

return (
<div className="page">
<a className="skip-link" href="#main">
Skip to main content
</a>

<header className="hero" id="home">
<div className="orb orb-left" />
<div className="orb orb-right" />
<div className="nav container glass">
<div className="brand brand-with-slogan">
<div className="brand-main">
<svg className="brand-icon" viewBox="0 0 200 200" role="img" aria-label="USECHAT logo">
<polygon
points="70 40 130 40 160 70 160 130 130 160 70 160 40 130 40 70"
fill="none"
stroke="#ffffff"
strokeWidth="10"
strokeLinejoin="round"
/>
<text
x="100"
y="118"
textAnchor="middle"
fontSize="48"
fontWeight="700"
fill="#ffffff"
fontFamily="'Plus Jakarta Sans', 'Inter', sans-serif"
>
U
</text>
<g stroke="#ffffff" strokeWidth="10" strokeLinecap="round" fill="none">
<line x1="80" y1="40" x2="80" y2="18" />
<line x1="120" y1="40" x2="120" y2="18" />
<line x1="40" y1="85" x2="18" y2="85" />
<line x1="40" y1="115" x2="18" y2="115" />
<line x1="160" y1="85" x2="182" y2="85" />
<line x1="160" y1="115" x2="182" y2="115" />
<line x1="100" y1="160" x2="100" y2="182" />
</g>
<g fill="#ffffff">
<circle cx="80" cy="12" r="9" />
<circle cx="120" cy="12" r="9" />
<circle cx="12" cy="85" r="9" />
<circle cx="12" cy="115" r="9" />
<circle cx="188" cy="85" r="9" />
<circle cx="188" cy="115" r="9" />
<circle cx="100" cy="188" r="9" />
</g>
</svg>
<span className="brand-text">USECHAT</span>
</div>
<span className="brand-slogan">Equity-Built Governance Structure for Abundance</span>
</div>

<nav className={`nav-links ${navOpen ? 'open' : ''}`} aria-label="Primary">
<a href="#about" onClick={closeNav}>About</a>
<a href="#technology" onClick={closeNav}>Technology</a>
<a href="#mvp" onClick={closeNav}>MVP</a>
<a href="#licensing" onClick={closeNav}>Licensing</a>
<a href="#impact" onClick={closeNav}>Impact</a>
<a href="#spirituality" onClick={closeNav}>Spirituality</a>
<a href="#investors" onClick={closeNav}>Investors</a>
<a href="#resources" onClick={closeNav}>Resources</a>
<a href="#contact" onClick={closeNav}>Contact</a>
</nav>

<div className="nav-ctas">
<a className="btn ghost" href="#contact">
Contact Counsel
</a>
<a className="btn solid glow" href="#investors">
Request Funding
</a>
<button
className={`hamburger ${navOpen ? 'open' : ''}`}
aria-label="Toggle navigation"
aria-expanded={navOpen}
onClick={() => setNavOpen(!navOpen)}
type="button"
>
<span />
<span />
<span />
</button>
</div>
</div>

<div className="hero-grid container" id="main">
<motion.div variants={fadeUp} initial="hidden" animate="visible" className="hero-copy">
<p className="eyebrow">Patent-pending fairness engine</p>
<h1>Fairness by Design - Governance-Grade AI</h1>
<p className="lead">
USECHAT is a multi-engine AI system that makes decisions transparent, mathematically fair, and auditable.
Built for platform licensing and government adoption.
</p>
<div className="cta-row">
<a className="btn solid glow" href="#investors">
Request Funding
</a>
<a className="btn outline" href="#contact">
Request Demo
</a>
</div>
<p className="tagline">
Patent-protected fairness infrastructure that reduces bias, restores trust, and enables compliant allocation at scale.
</p>
</motion.div>

<motion.div variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.1 }} className="hero-card glass">
<div className="card-header">Seven engines, one governance layer</div>
<ul>
<li>Mathematical fairness constraint at the core.</li>
<li>Transparent eligibility and redistribution logic.</li>
<li>Audit-grade monitoring for regulators and licensees.</li>
</ul>
<div className="card-footer">
<span className="badge">MVP May 2026</span>
<span className="badge gold">Patent filing in progress</span>
</div>
</motion.div>
</div>
</header>

<section className="section value-grid" aria-labelledby="value-grid-title">
<div className="container">
<div className="section-header">
<h2 id="value-grid-title">Value for every stakeholder</h2>
<p className="section-sub">Built for platforms, governments, and funders.</p>
</div>
<div className="grid">
{quickValues.map((item, idx) => (
<motion.article
key={item.title}
className="card lift"
variants={fadeUp}
initial="hidden"
whileInView="visible"
viewport={{ once: true, margin: '-50px' }}
transition={{ delay: idx * 0.1 }}
>
<h3>{item.title}</h3>
<p>{item.text}</p>
</motion.article>
))}
</div>
</div>
</section>

<section className="trust-bar">
<div className="container trust-inner">
<motion.div variants={fadeIn} initial="hidden" whileInView="visible" viewport={{ once: true }}>
Supported by a rigorous technical patent, a licensing-first business model, and a clear MVP timeline (Mar-May 2026).
</motion.div>
<div className="trust-icons" aria-label="Trust markers">
<span className="pill">Patent</span>
<span className="pill">MVP</span>
<span className="pill">Licensing</span>
</div>
</div>
</section>

<section className="cta-banner">
<div className="container cta-inner">
<p>We are raising $100,000 USD to complete patent filing and the 3-engine MVP.</p>
<div className="cta-row">
<a className="btn solid glow" href="#investors">
Request Funding
</a>
</div>
</div>
</section>

<section className="section about" id="about" aria-labelledby="about-title">
<div className="container">
<div className="section-header">
<h2 id="about-title">About USECHAT (Pty) Limited</h2>
<p className="section-sub">Governance-grade AI for fair, auditable, and human-centered decisions.</p>
</div>
<div className="two-col">
<motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
<p>USECHAT is a digital governance system built to ensure that fairness is not assumed, but enforced. It is a Governance-grade AI for fair, auditable, and human-centered decisions.</p>
<p>By applying equity-based constraints, auditable logic, and transparent decision pathways, USECHAT evaluates participation and eligibility in a structured manner, enabling systems where small contributions can collectively lead to sustainable and inclusive outcomes.</p>
<p>USECHAT does not replace human judgment. It strengthens it with structure, visibility, and accountability.</p>
<p>USECHAT is designed for acquisition and licensing to:</p>
<ul className="bullet">
<li>Global digital platforms</li>
<li>Governments, and</li>
<li>Public institutions</li>
</ul>
<p>The USECHAT system supports transparent governance, eligibility enforcement, and accountable decision-making at scale.</p>
</motion.div>

<motion.div
variants={fadeUp}
initial="hidden"
whileInView="visible"
viewport={{ once: true }}
transition={{ delay: 0.1 }}
className="about-panel"
>
<h4>Vision</h4>
<p>To become the world's leading trust layer for governance-grade AI, enabling fair, auditable decisions at scale.</p>
<h4>Mission</h4>
<p>To build and operate a policy-neutral trust layer for governance-grade AI that enables transparent, auditable, and fair decisions, accelerating national upliftment for citizens, platforms, and institutions.</p>
<h4>Purpose</h4>
<p>Where fairness becomes enforceable</p>
<h4>Values</h4>
<ul className="bullet">
<li>Fairness</li>
<li>Governance</li>
<li>Accountability</li>
<li>Transparency</li>
<li>Ethical</li>
</ul>
<div className="tagline-box">Fairness, transparency, and trust for every decision.</div>
</motion.div>
</div>
</div>
</section>

<section className="section technology" id="technology" aria-labelledby="tech-title">
<div className="container">
<div className="section-header">
<h2 id="tech-title">The Technology - Seven Engines, One Governance Layer</h2>
<p className="section-sub">Patent-pending architecture that enforces fairness mathematically.</p>
</div>

<div className="tech-grid">
<motion.div
variants={fadeUp}
initial="hidden"
whileInView="visible"
viewport={{ once: true }}
className="diagram glass"
aria-label="Engine diagram"
>
<svg className="patent-diagram" viewBox="0 0 600 600" role="img" aria-label="USECHAT patent architecture">
<polygon
points="150,60 450,60 540,300 450,540 150,540 60,300"
fill="rgba(255,255,255,0.04)"
stroke="rgba(255,255,255,0.08)"
strokeWidth="2"
strokeLinejoin="round"
/>
<g className="afce-core">
<rect x="240" y="260" width="120" height="80" rx="16" ry="16" />
<text x="300" y="308" textAnchor="middle">AFCE</text>
<title>AFCE - Enforces fairness constraints across all engines.</title>
</g>
<g className="engine-ring">
{diagramNodes.map((node, idx) => {
const angle = idx * (360 / diagramNodes.length);
const radius = 175;
const rad = (angle * Math.PI) / 180;
const cx = 300 + radius * Math.cos(rad);
const cy = 300 + radius * Math.sin(rad);
return (
<g
key={node.id}
className="engine-node"
transform={`translate(${cx},${cy}) rotate(${angle})`}
onMouseEnter={() => setActiveEngine(node.id)}
onFocus={() => setActiveEngine(node.id)}
>
<rect x="-69" y="-25" rx="14" ry="14" width="138" height="50" fill={node.color} />
<text x="0" y="5" textAnchor="middle">{node.label}</text>
<title>{node.tooltip}</title>
</g>
);
})}
</g>
</svg>
</motion.div>

<motion.div
variants={fadeUp}
initial="hidden"
whileInView="visible"
viewport={{ once: true }}
transition={{ delay: 0.15 }}
className="engine-detail"
>
<p className="eyebrow">Engine details</p>
<h3>{active.name}</h3>
<p className="engine-line">{active.line}</p>
<p className="engine-note">{active.note}</p>
<div className="engine-actions">
<a className="btn outline" href={pdfHref('5')} target="_blank" rel="noreferrer">
Download PDF
</a>
<a className="btn solid glow" href="#contact">
Request Technical Brief
</a>
</div>
</motion.div>
</div>

<div className="engine-list">
{engines.map((engine, idx) => (
<motion.div
key={engine.id}
className="engine-card"
variants={fadeUp}
initial="hidden"
whileInView="visible"
viewport={{ once: true, margin: '-40px' }}
transition={{ delay: (idx % 3) * 0.08 }}
>
<h4>{engine.name}</h4>
<p>{engine.line}</p>
<p className="engine-note small">{engine.note}</p>
</motion.div>
))}
</div>

<div className="engine-caption">
<p>
The 7-engines operate under a unified governance core (TWO):
<br />- Thanksgiving defines purpose
<br />- Wisdom governs decision logic, and
<br />- Obedience delivers provision.
</p>
</div>

<div className="how-it-works">
<h3>How It Works: From Ethics to Outcomes</h3>
<p>
USECHAT operates through a structured flow. Ethical orientation (Thanksgiving) shapes system intent. Governance logic
(Wisdom) determines how rules and fairness constraints are applied. Faithful execution (Obedience) delivers provision-clear,
auditable outcomes for users and institutions. This structure ensures that technology serves dignity, not just efficiency.
</p>
<button className="btn outline" type="button">
How It Works (PDF)
</button>
</div>
</div>
</section>

<section className="section mvp" id="mvp" aria-labelledby="mvp-title">
<div className="container">
<div className="section-header">
<h2 id="mvp-title">MVP: Three Engines, Acquisition-Ready Demo (May 2026)</h2>
<p className="section-sub">
MVP focuses on the critical subset: Transformation, Eligibility, and AFCE. Remaining engines are disclosed under NDA or
licensing.
</p>
</div>
<div className="two-col">
<motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
<ul className="bullet">
<li>Transformation Engine - input normalization and feature mapping.</li>
<li>Eligibility Engine - rule-driven outcomes with pass or fail and reason codes.</li>
<li>AFCE - fairness enforcement, adjustments, and transparent explanation text.</li>
</ul>
<div className="cta-row">
<a className="btn solid glow" href="#contact">
Request Demo Access
</a>
<a className="btn outline" href="#investors">
Request Funding
</a>
</div>
</motion.div>

<motion.div
variants={fadeUp}
initial="hidden"
whileInView="visible"
viewport={{ once: true }}
transition={{ delay: 0.15 }}
className="timeline"
>
{roadmap.map((item) => (
<div key={item.month} className="timeline-item">
<div className="timeline-dot" aria-hidden="true" />
<div>
<p className="timeline-month">{item.month}</p>
<p className="timeline-detail">{item.detail}</p>
</div>
</div>
))}
</motion.div>
</div>
</div>
</section>

<section className="section licensing" id="licensing" aria-labelledby="licensing-title">
<div className="container">
<div className="section-header">
<h2 id="licensing-title">Licensing Model - Built for Sustainable Impact and Compliance</h2>
<p className="section-sub">
Annual per-subscriber royalty (baseline 0.50 USD per subscriber per year) or revenue-share alternatives. Public-sector terms
available.
</p>
</div>
<div className="grid">
{licensingPillars.map((pillar, idx) => (
<motion.article
key={pillar.title}
className="card lift"
variants={fadeUp}
initial="hidden"
whileInView="visible"
viewport={{ once: true }}
transition={{ delay: idx * 0.08 }}
>
<h3>{pillar.title}</h3>
<p>{pillar.text}</p>
</motion.article>
))}
</div>

<motion.div
variants={fadeUp}
initial="hidden"
whileInView="visible"
viewport={{ once: true }}
transition={{ delay: 0.12 }}
className="licensing-note glass"
>
<p>
Government partnership: pilot model, joint evaluation, local oversight, and transparency reporting. Example: South Africa pilot
for housing and welfare eligibility.
</p>
<a className="btn solid glow" href="#contact">
Contact Licensing Team
</a>
</motion.div>
</div>
</section>

<section className="section impact" id="impact" aria-labelledby="impact-title">
<div className="container">
<div className="section-header">
<h2 id="impact-title">Measurable Impact - Designed for Inequality Reduction</h2>
<p className="section-sub">
First public-facing impact focus: South Africa housing eligibility, social grants, and youth programs.
</p>
</div>
<div className="two-col">
<motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
<h4>Case example</h4>
<p className="bold">Problem:</p>
<p>Opaque housing allocation exacerbates social tension.</p>
<p className="bold">USECHAT approach:</p>
<p>Transparent eligibility plus fairness constraints for equitable allocation and an audit trail.</p>
<p className="bold">KPIs:</p>
<ul className="bullet">
<li>Reduction in allocation disparity.</li>
<li>Percent of households correctly classified for assistance.</li>
<li>Time-to-decision improvements.</li>
</ul>
</motion.div>

<motion.div
variants={fadeUp}
initial="hidden"
whileInView="visible"
viewport={{ once: true }}
transition={{ delay: 0.1 }}
className="impact-panel"
>
<p className="bold">Data and measurement</p>
<ul className="bullet">
<li>Baseline and post-deployment fairness metrics (Gini trend, group disparity index).</li>
<li>Monitoring dashboard with metric snapshots in the demo.</li>
</ul>
<a className="btn outline" href="#contact">
Request Government Briefing
</a>
</motion.div>
</div>
</div>
</section>

<section className="section spirituality" id="spirituality" aria-labelledby="spirituality-title">
<div className="container">
<div className="section-header">
<h2 id="spirituality-title">Faith and Purpose - The Spiritual Heart of USECHAT</h2>
<p className="section-sub">Technology must serve human dignity, not merely efficiency.</p>
</div>
<div className="two-col">
<motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
<p>
USECHAT is guided by a moral and ethical framework inspired by a recurring pattern evident in the ministry of Jesus
Christ-particularly in moments where limited resources produced just and abundant outcomes. This pattern is captured in the
guiding framework we call TWO: Thanksgiving. Wisdom. Obedience.
</p>
<p>
These principles are not symbolic language, nor are they interchangeable. Each plays a distinct role in shaping how USECHAT
is conceived, governed, and executed.
</p>

<p className="bold">
<strong>Thanksgiving</strong> - Ethical Orientation and Stewardship
</p>
<p>Thanksgiving is the starting posture, not a system operation.</p>

<p className="bold">
<strong>Wisdom</strong> - Governance, Discernment, and Constraint
</p>

<p className="bold">
<strong>Obedience</strong> - Faithful Execution and Provision
</p>
</motion.div>

<motion.div
variants={fadeUp}
initial="hidden"
whileInView="visible"
viewport={{ once: true }}
transition={{ delay: 0.1 }}
className="spirit-card"
>
<p className="bold">TWO in Summary</p>
<ul className="bullet">
<li>Thanksgiving shapes ethical posture and stewardship.</li>
<li>Wisdom governs system design and decision logic.</li>
<li>Obedience executes governed decisions and provides just outcomes.</li>
</ul>
<p>
Together, these principles ensure that USECHAT remains technically rigorous, ethically grounded, and socially responsible.
This is not theology embedded in code. It is values translated into disciplined system design.
</p>
</motion.div>
</div>
</div>
</section>

<section className="section investors" id="investors" aria-labelledby="investors-title">
<div className="container">
<div className="section-header">
<h2 id="investors-title">Why Fund USECHAT?</h2>
<p className="section-sub">Social impact, IP protection, and commercial licensing upside.</p>
</div>
<div className="grid">
<motion.article variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="card lift">
<h3>Funding summary</h3>
<p>
We seek $100,000 USD to complete USPTO filing, deliver a three-engine MVP, and prepare licensing materials. Request the funding
brief to review terms.
</p>
<ul className="bullet">
<li>Patent filing and protection</li>
<li>3-engine MVP delivery and demo readiness</li>
<li>Licensing materials and partner onboarding</li>
<li>Timeline: March-May 2026</li>
</ul>
<div className="cta-row">
<button
  type="button"
  className="btn solid glow"
  onClick={() => setFundingBriefOpen(true)}
>
  View Funding Brief
</button>
<a className="btn outline" href="#contact">
Contact Investor Relations
</a>
</div>
</motion.article>

<motion.article
variants={fadeUp}
initial="hidden"
whileInView="visible"
viewport={{ once: true }}
transition={{ delay: 0.1 }}
className="card lift"
>
<h3>Investor highlights</h3>
<ul className="bullet">
<li>Patent-protected, high-barrier IP in fairness tech.</li>
<li>Lean MVP ready May 2026 with fast licensing path.</li>
<li>High revenue potential via per-subscriber royalties.</li>
<li>Direct relevance to global regulators and governments.</li>
<li>Clear monitoring and measurable KPIs.</li>
</ul>
</motion.article>
</div>
</div>
</section>

<section className="section team" id="team" aria-labelledby="team-title">
<div className="container">
<div className="section-header">
<h2 id="team-title">Team and Advisors</h2>
<p className="section-sub">Inventor-led team with licensing and legal expertise.</p>
</div>
<div className="grid">
{team.map((member, idx) => (
<motion.article
key={member.name}
className="card lift"
variants={fadeUp}
initial="hidden"
whileInView="visible"
viewport={{ once: true }}
transition={{ delay: (idx % 3) * 0.08 }}
>
<h3>{member.name}</h3>
<p>{member.role}</p>
{member.bio && <p className="engine-note small">{member.bio}</p>}
</motion.article>
))}
</div>
</div>
</section>

{/* =========================
PRESS & RESOURCES (UPDATED)
========================= */}
<section id="resources" className="section">
<div className="container">
<header className="section-header">
<h2>PRESS &amp; RESOURCES</h2>
<div className="press-kicker pill">USECHAT</div>
<p className="section-sub">
Official documentation supporting technical validation, governance
legitimacy, policy alignment, investor diligence, and public understanding
of the USECHAT invention.
</p>
<p className="section-sub">
Documents are arranged in order of institutional importance, progressing
from enforceable technical authority to public-facing context.
</p>
</header>

{/* SECTION 1 */}
<div className="press-block">
<h3 className="press-block-title">SECTION 1 — CORE TECHNICAL DOCUMENTS</h3>
<p className="press-block-sub">(System Authority &amp; Compliance)</p>

<p className="press-label">Purpose:</p>
<blockquote className="press-purpose">
Defines how USECHAT operates, how it enforces governance, and how it
complies with professional, legal, and ethical standards.
</blockquote>

<div className="press-grid">
{[
{ title: '1.1 Systems Engineering Compliance Statement', desc: 'Formal confirmation that USECHAT adheres to professional systems engineering principles, including modularity, scalability, auditability, and institutional governance.', key: '1' },
{ title: '1.2 Responsible AI Compliance Statement', desc: 'Declaration of USECHAT\'s compliance with responsible AI principles, including fairness safeguards, transparency, accountability, and human oversight.', key: '2' },
{ title: '1.3 MVP Technical Specification', desc: 'Technical description of the minimum viable deployment demonstrating system logic, governance enforcement, and readiness for national-scale licensing.', key: '3' },
{ title: '1.4 Patent Summary', desc: 'High-level, non-confidential overview of the patented system scope and governance architecture (updated PDF).', key: '5' },
{ title: '1.5 Patent Summary (Technical Abstract Version)', desc: 'High-level, non-confidential overview of the patented system scope and governance architecture (Section 1.5 technical abstract).', key: '1.5' },
{ title: '4.1 - USECHAT Alignment with South Africa\'s National Development Plan (NDP)', desc: 'Policy-facing document showing how USECHAT enforces national development objectives.', key: '4.1' },
{ title: '4.2 - USECHAT Universal Government Value & Application Summary', desc: 'Policy-facing document showing how USECHAT enforces national development objectives.', key: '4.2' },
{ title: '4.3 - USECHAT One Page Executive Brief', desc: 'Policy-facing document showing how USECHAT enforces national development objectives.', key: '4.3' },
{ title: '4.4 - USECHAT Ministerial Talking Points', desc: 'Policy-facing document showing how USECHAT enforces national development objectives.', key: '4.4' },
{ title: '4.5 - USECHAT Treasury & Finance Justification Note', desc: 'Policy-facing document showing how USECHAT enforces national development objectives.', key: '4.5' },
{ title: '5. Patent Summary', desc: 'High-level, non-confidential overview of the patented system scope and governance architecture, suitable for regulators and institutional reviewers. Updated to the Section 1.5 technical abstract PDF.', key: '5' }
].map((doc) => (
<motion.article
key={doc.title}
className="card lift press-card"
variants={fadeUp}
initial="hidden"
whileInView="visible"
viewport={{ once: true, margin: '-40px' }}
>
<h4 className="press-item-title">{doc.title}</h4>
<p className="press-label">Short description:</p>
<blockquote>{doc.desc}</blockquote>
<a className="btn outline" href={pdfHref(doc.key)} target="_blank" rel="noreferrer">
Download PDF
</a>
</motion.article>
))}
</div>
</div>

<hr className="press-divider" />

{/* SECTION 2 */}
<div className="press-block">
<h3 className="press-block-title">SECTION 2 — INVESTOR &amp; FUNDING MATERIALS</h3>
<p className="press-block-sub">(Economic &amp; Commercial Evaluation)</p>

<p className="press-label">Purpose:</p>
<blockquote className="press-purpose">
Supports funding decisions, valuation review, commercialization strategy,
and macro-economic impact assessment.
</blockquote>

<div className="press-grid">
{/* ✅ UPDATED: Funding Brief now opens Use of Funds image (modal) */}
<motion.article className="card lift press-card" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
<h4 className="press-item-title">1. Funding Brief</h4>
<p className="press-label">Short description:</p>
<blockquote>Overview of funding requirements, deployment phases, and use-of-funds logic.</blockquote>
<button type="button" className="btn outline" onClick={() => setUseOfFundsOpen(true)}>
View Document
</button>
</motion.article>

<motion.article className="card lift press-card" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
<h4 className="press-item-title">2. Funding Timeline</h4>
<p className="press-label">Short description:</p>
<blockquote>Development &amp; commercialization timeline (Gantt chart) for funding and delivery milestones.</blockquote>
<button type="button" className="btn outline" onClick={() => setFundingChartOpen(true)}>
View Chart
</button>
</motion.article>

<motion.article className="card lift press-card" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
<h4 className="press-item-title">3. Job Creation &amp; Economic Impact Brief</h4>
<p className="press-label">Short description:</p>
<blockquote>Analysis of employment creation, productivity uplift, and macro-economic benefits arising from national deployment of USECHAT.</blockquote>
<a className="btn outline" href={pdfHref('7')} target="_blank" rel="noreferrer">
Download PDF
</a>
</motion.article>

<motion.article className="card lift press-card" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
<h4 className="press-item-title">4. Valuation &amp; Commercialization Overview</h4>
<p className="press-label">Short description:</p>
<blockquote>High-level analysis of licensing, scaling, and long-term commercial value pathways.</blockquote>
<a className="btn outline" href={pdfHref('8')} target="_blank" rel="noreferrer">
Download PDF
</a>
</motion.article>

<motion.article className="card lift press-card" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
<h4 className="press-item-title">5. Acquisition &amp; Licensing Strategy Overview</h4>
<p className="press-label">Short description:</p>
<blockquote>Framework comparing licensing versus acquisition scenarios for governments and strategic technology partners.</blockquote>
<a className="btn outline" href={pdfHref('9')} target="_blank" rel="noreferrer">
Download PDF
</a>
</motion.article>
</div>
</div>

<hr className="press-divider" />

{/* SECTION 3 */}
<div className="press-block">
<h3 className="press-block-title">SECTION 3 — MEDIA &amp; DEMONSTRATION</h3>
<p className="press-block-sub">(Visual Proof &amp; System Visibility)</p>

<p className="press-label">Purpose:</p>
<blockquote className="press-purpose">
Provides visual and functional representations without exposing proprietary logic.
</blockquote>

<div className="press-grid">
<article className="card lift press-card">
<h4 className="press-item-title">3.1 ■ USECHAT Architecture Diagram</h4>
<p className="press-label">Short description:</p>
<blockquote>High-level visual depiction of system layers and governance flow.</blockquote>

<button
type="button"
className="btn outline"
onClick={() => setPressDiagramOpen(true)}
>
View Diagram
</button>
</article>

<article className="card lift press-card">
<h4 className="press-item-title">■ USECHAT Demonstration Video</h4>
<p className="press-label">Short description:</p>
<blockquote>
Walkthrough of system behavior, contribution flow, and entitlement logic at a conceptual level.
</blockquote>

<a className="btn outline disabled" href={DEMO_VIDEO_URL} onClick={(e) => e.preventDefault()}>
Watch Video
</a>
<p className="press-note">Video link to be added.</p>
</article>
</div>
</div>

<hr className="press-divider" />

{/* SECTION 4 (UPDATED) */}
<div className="press-block">
<h3 className="press-block-title">SECTION 4 — FOUNDATIONAL DOCUMENTS &amp; CONTEXT</h3>
<p className="press-block-sub">(Legitimacy, Ethics &amp; Societal Rationale)</p>

<p className="press-label">Purpose:</p>
<blockquote className="press-purpose">
Explains why USECHAT exists, the governance philosophy behind it, and the ethical and societal principles
that justify its national use.
</blockquote>

<div className="press-grid">
{[
{
title: 'A.1. WHAT IS USECHAT',
desc: 'A global infrastructure, which ensures that eligibility is earned through consistency and integrity, creating a sustainable ecosystem of prosperity.',
kind: 'pdf',
key: 'A1'
},
{
title: 'A.2.A. Triple-Layer Master Blueprint',
desc: 'Conceptual framework explaining the separation of contribution, governance enforcement, and provision layers.',
kind: 'pdf',
key: '12'
},
{
title: 'A.2.B. Triple-Layer Master Blueprint (Diagram)',
desc: 'This diagram represents a conceptual values framework and does not describe operational logic, eligibility rules, or system implementation. USECHAT remains policy-neutral and institutionally governed (Disclaimer).',
kind: 'diagram'
},
{
title: 'A.3. Foundational Governance Rationale',
desc: 'Explanation of the governance challenges, principles, and institutional logic that led to the creation of USECHAT.',
kind: 'pdf',
key: '13'
},
{
title: 'B.4. Inequality & Fairness Framework',
desc: 'Conceptual analysis of inequality, contribution-based fairness, and structural correction without welfare expansion.',
kind: 'pdf',
key: '14'
},
{
title: 'C.5. Ethical & Societal Context Brief',
desc: 'Discussion of ethics, public trust, legitimacy, human dignity, and long-term national upliftment considerations.',
kind: 'pdf',
key: '15'
},
{
title: 'D.6. Founder’s Statement',
desc: 'Personal vision and motivation behind the invention, separated clearly from governance authority.',
kind: 'pdf',
key: '16'
},
{
title: 'E.7. Storytelling Document',
desc: 'Narrative explanation of the societal problem USECHAT addresses and the journey toward its creation.',
kind: 'pdf',
key: '17'
}
].map((doc) => (
<article key={doc.title} className="card lift press-card">
<h4 className="press-item-title">{doc.title}</h4>
<p className="press-label">Short description:</p>
<blockquote>{doc.desc}</blockquote>

{doc.kind === 'diagram' ? (
<button type="button" className="btn outline" onClick={() => setBlueprintDiagramOpen(true)}>
View Diagram
</button>
) : (
<a className="btn outline" href={pdfHref(doc.key)} target="_blank" rel="noreferrer">
Download PDF
</a>
)}
</article>
))}
</div>
</div>

<hr className="press-divider" />

{/* SECTION 5 */}
<div className="press-block">
<h3 className="press-block-title">SECTION 5 — PUBLIC COMMUNICATION &amp; PRESS</h3>
<p className="press-block-sub">(Public-Facing Information)</p>

<p className="press-label">Purpose:</p>
<blockquote className="press-purpose">
High-level, non-technical communications suitable for media and general audiences.
</blockquote>

<div className="press-grid">
<article className="card lift press-card">
<h4 className="press-item-title">■ USECHAT Public Press Announcement</h4>
<p className="press-label">Short description:</p>
<blockquote>
Official public statement introducing USECHAT as a national digital governance framework for fairness,
opportunity, and transparency.
</blockquote>

<a className="btn outline" href={pdfHref('18')} target="_blank" rel="noreferrer">
Download PDF
</a>
</article>
</div>
</div>
</div>
</section>

<section className="section contact" id="contact" aria-labelledby="contact-title">
<div className="container">
<div className="section-header">
<h2 id="contact-title">Contact</h2>
<p className="section-sub">Contact us for funding, licensing, or partnership.</p>
</div>
<div className="two-col">
<motion.form
variants={fadeUp}
initial="hidden"
whileInView="visible"
viewport={{ once: true }}
className="contact-form"
onSubmit={handleFormSubmit}
>
<input type="text" name="hp_token" tabIndex={-1} autoComplete="off" aria-hidden="true" className="honeypot" />
<label>
Organization name
<input name="organization" type="text" required />
</label>
<label>
Contact person and role
<input name="contact" type="text" required />
</label>
<label>
Email
<input name="email" type="email" required />
</label>
<label>
Phone (optional)
<input name="phone" type="tel" />
</label>
<label>
Country
<input name="country" type="text" required />
</label>
<label>
Inquiry type
<select name="inquiry" defaultValue="Funding" required>
<option>Funding</option>
<option>Licensing</option>
<option>Government Pilot</option>
<option>Press</option>
<option>Careers</option>
</select>
</label>
<label>
Brief message (1000 chars)
<textarea name="message" maxLength={1000} rows={4} required />
</label>
<label>
Upload (PDF optional)
<input name="attachment" type="file" accept=".pdf" />
</label>
<p className="privacy-note">
We will treat submitted data as confidential and will sign NDAs where required.
</p>
<button type="submit" className="btn solid glow" disabled={submitStatus.state === "sending"}>
{submitStatus.state === "sending" ? "Sending..." : "Submit"}
</button>

{submitStatus.state === "success" && (
<p className="privacy-note">Thanks — your message was sent.</p>
)}

{submitStatus.state === "error" && (
<p className="privacy-note">Couldn’t send: {submitStatus.error}</p>
)}
</motion.form>

<motion.div
variants={fadeUp}
initial="hidden"
whileInView="visible"
viewport={{ once: true }}
transition={{ delay: 0.1 }}
className="contact-info"
>
<p>Contact email:</p>
<p className="contact-single">
<a href="mailto:contact@usechat.systems">contact@usechat.systems</a>
</p>
<p className="privacy-note">GDPR/POPIA: data used only for requested follow-up.</p>
<div className="cta-row">
<a className="btn outline" href="#investors">
Funding Request
</a>
<a className="btn outline" href="#licensing">
Licensing Inquiry
</a>
</div>
</motion.div>
</div>
</div>
</section>

<section className="section privacy" id="privacy" aria-labelledby="privacy-title">
<div className="container">
<div className="section-header">
<h2 id="privacy-title">Privacy, POPIA & GDPR</h2>
<p className="section-sub">How we handle data for funding, licensing, government, and press inquiries.</p>
</div>
<div className="two-col">
<div>
<ul className="bullet">
<li>Data used only to respond to the submitted inquiry (funding, licensing, government, press, careers).</li>
<li>Data stored securely; access limited to authorized team members and counsel.</li>
<li>We will sign NDAs on request and honor lawful deletion requests.</li>
<li>No sale of data; no unrelated marketing.</li>
</ul>
</div>
<div className="about-panel">
<p className="bold">Your rights</p>
<ul className="bullet">
<li>Request deletion or correction of your data.</li>
<li>Request a copy of your submitted information.</li>
<li>Opt out of non-essential communications at any time.</li>
</ul>
<a className="btn outline" href="#contact">Contact Data Officer</a>
</div>
</div>
</div>
</section>

<footer className="footer">
<div className="container footer-inner">
<div>
<div className="brand">
<svg className="brand-icon" viewBox="0 0 200 200" role="img" aria-label="USECHAT logo">
<polygon
points="70 40 130 40 160 70 160 130 130 160 70 160 40 130 40 70"
fill="none"
stroke="#ffffff"
strokeWidth="10"
strokeLinejoin="round"
/>
<text
x="100"
y="118"
textAnchor="middle"
fontSize="48"
fontWeight="700"
fill="#ffffff"
fontFamily="'Plus Jakarta Sans', 'Inter', sans-serif"
>
U
</text>
<g stroke="#ffffff" strokeWidth="10" strokeLinecap="round" fill="none">
<line x1="80" y1="40" x2="80" y2="18" />
<line x1="120" y1="40" x2="120" y2="18" />
<line x1="40" y1="85" x2="18" y2="85" />
<line x1="40" y1="115" x2="18" y2="115" />
<line x1="160" y1="85" x2="182" y2="85" />
<line x1="160" y1="115" x2="182" y2="115" />
<line x1="100" y1="160" x2="100" y2="182" />
</g>
<g fill="#ffffff">
<circle cx="80" cy="12" r="9" />
<circle cx="120" cy="12" r="9" />
<circle cx="12" cy="85" r="9" />
<circle cx="12" cy="115" r="9" />
<circle cx="188" cy="85" r="9" />
<circle cx="188" cy="115" r="9" />
<circle cx="100" cy="188" r="9" />
</g>
</svg>
<span className="brand-text">USECHAT</span>
</div>
<p>Fairness by design. Governance-grade AI.</p>
</div>

<div className="footer-links">
<h4>Company</h4>
<a href="#home">Home</a>
<a href="#about">About</a>
<a href="#technology">Technology</a>
<a href="#resources">Resources</a>
<a href="#privacy">Privacy</a>
</div>

<div className="footer-links">
<h4>Engage</h4>
<a href="#investors">Investors</a>
<a href="#licensing">Licensing</a>
<a href="#impact">Impact</a>
<a href="#contact">Contact</a>
</div>

<div className="footer-ctas">
<a className="btn outline" href="#contact">
Request Demo
</a>
<a className="btn solid glow" href="#investors">
Request Funding
</a>
</div>
</div>
</footer>

{/* =========================
WELCOME POPUP
========================= */}
{welcomeOpen && (
<div className="usechat-popup" role="dialog" aria-label="Welcome to USECHAT">
<div className="usechat-popup__top">
<strong className="usechat-popup__title">Welcome to USECHAT!</strong>
<button className="usechat-popup__close" type="button" onClick={closeWelcome} aria-label="Close">
✕
</button>
</div>

<p className="usechat-popup__text">
We’re delighted to have you here. If you need any help, simply send us a message — we’re online and ready to assist.
</p>

<button type="button" onClick={openWhatsAppChat} className="usechat-popup__btn">
  💬 Start Chat on WhatsApp
</button>
</div>
)}

{/* Optional sound (place soft-message-chime.mp3 in /public) */}
<audio ref={welcomeSoundRef} src={`${process.env.PUBLIC_URL}/soft-message-chime.mp3`} preload="auto" />

{/* =========================
MODAL: Architecture Diagram (ORBIT)
========================= */}
{pressDiagramOpen && (
<div
className="modal-overlay"
role="dialog"
aria-modal="true"
aria-label="USECHAT Architecture Diagram"
onClick={() => setPressDiagramOpen(false)}
>
<div className="modal-content" onClick={(e) => e.stopPropagation()}>
<div className="modal-header">
<h3 className="modal-title">USECHAT Architecture Diagram</h3>
<button className="modal-close" type="button" onClick={() => setPressDiagramOpen(false)} aria-label="Close modal">
×
</button>
</div>

<div className="usechat-orbit-container">
<div className="orbit-system" role="img" aria-label="USECHAT interactive architecture diagram">
<div className="engine center afce">
AFCE<br />
<span>
Algorithmic Fairness<br />
Constraint Engine
</span>
</div>

<div className="orbit-rotator" aria-hidden="true">
<div className="engine orbit e1">Contribution<br />Intake</div>
<div className="engine orbit e2">Verification</div>
<div className="engine orbit e3">Normalization</div>
<div className="engine orbit e4">Eligibility<br />Determination</div>
<div className="engine orbit e5">Provision<br />Routing</div>
<div className="engine orbit e6">Audit &amp;<br />Oversight</div>
</div>
</div>

<p className="orbit-tip">Tip: Hover to pause rotation. Press ESC to close.</p>
</div>
</div>
</div>
)}

{/* =========================
MODAL: Funding Timeline Chart
========================= */}
{fundingChartOpen && (
<div
className="modal-overlay"
role="dialog"
aria-modal="true"
aria-label="Funding Timeline Chart"
onClick={() => setFundingChartOpen(false)}
>
<div className="modal-content" onClick={(e) => e.stopPropagation()}>
<div className="modal-header">
<h3 className="modal-title">Funding Timeline</h3>
<button className="modal-close" type="button" onClick={() => setFundingChartOpen(false)} aria-label="Close modal">
×
</button>
</div>

<img
className="modal-media modal-media--contain"
src={fundingTimelineImg}
alt="Development and Commercialization Timeline chart"
/>

<p className="modal-hint">Press ESC to close.</p>
</div>
</div>
)}

{/* =========================
MODAL: Triple-Layer Blueprint Diagram
========================= */}
{blueprintDiagramOpen && (
<div
className="modal-overlay"
role="dialog"
aria-modal="true"
aria-label="Triple-Layer Master Blueprint Diagram"
onClick={() => setBlueprintDiagramOpen(false)}
>
<div className="modal-content" onClick={(e) => e.stopPropagation()}>
<div className="modal-header">
<h3 className="modal-title">Triple-Layer Master Blueprint (Diagram)</h3>
<button className="modal-close" type="button" onClick={() => setBlueprintDiagramOpen(false)} aria-label="Close modal">
×
</button>
</div>

<img
className="modal-media modal-media--contain"
src={tripleLayerBlueprintDiagramImg}
alt="Triple-Layer Master Blueprint diagram"
/>

<p className="modal-hint">Press ESC to close.</p>
</div>
</div>
)}

{/* =========================
MODAL: Use of Funds (Funding Brief replacement)
========================= */}
{useOfFundsOpen && (
<div
className="modal-overlay"
role="dialog"
aria-modal="true"
aria-label="Use of Funds Document"
onClick={() => setUseOfFundsOpen(false)}
>
<div className="modal-content" onClick={(e) => e.stopPropagation()}>
<div className="modal-header">
<h3 className="modal-title">Use of Funds</h3>
<button className="modal-close" type="button" onClick={() => setUseOfFundsOpen(false)} aria-label="Close modal">
×
</button>
</div>

<img
className="modal-media modal-media--contain"
src={useOfFundsImg}
alt="USECHAT Use of Funds document"
/>

<p className="modal-hint">Press ESC to close.</p>
</div>
</div>
)}

{fundingBriefOpen && (
  <div
    className="modal-overlay"
    role="dialog"
    aria-modal="true"
    aria-label="Funding Brief"
    onClick={() => setFundingBriefOpen(false)}
  >
    <div className="modal-content wide" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h3 className="modal-title">Funding Brief</h3>
        <button
          className="modal-close"
          type="button"
          onClick={() => setFundingBriefOpen(false)}
          aria-label="Close modal"
        >
          ×
        </button>
      </div>

      <img
        className="modal-media modal-media--contain"
        src={fundingBriefImg}
        alt="Funding Brief"
      />

      <p className="modal-hint">Press ESC to close.</p>
    </div>
  </div>
)}

</div>
);
}

export default App;
